import { useEffect, useRef, useState } from "react";
import {
  COLORS,
  Size,
  SystemIcons,
  TextField,
} from "@laerdal/life-react-components";
import dayjs from "dayjs";
import { LocalStorageKey, useLocalStorage } from "../helpers/LocalStorage";

interface Props {
  // Used to inform this component to remove focus from time input
  removeFocusFromTimeInput: boolean;
  // Used to reset the remove focus variable in the parent state
  setRemoveFocusInParentComponent: (newValue: boolean) => void;
  // Used to set the time value into the latest time handled in the parent component
  setSelectedLatestTime?: (newValue: string) => void;
}

const TimePicker = ({
  removeFocusFromTimeInput,
  setRemoveFocusInParentComponent,
  setSelectedLatestTime,
}: Props) => {
  // Variables
  const [selectedInputValue, setSelectedInputValue] = useState(
    dayjs().format("HH:mm")
  );
  const [isInputTouched, setIsInputTouched] = useState(false);
  const [inputWithFocusTimestamp, setInputWithFocusTimestamp] = useLocalStorage(
    LocalStorageKey.InputWithFocusTimestamp,
    ""
  );
  const timePickerRef = useRef<HTMLInputElement | null>(null);
  const waitingMinutesBeforeResettingTimer = 10;

  // Functions
  const handleInputChange = (value: string) => {
    let maskedValue = value;

    // Adding mask (:)
    if (value.length === 3) {
      maskedValue = value.slice(0, 2);
      if (value.charAt(2) !== ":") {
        maskedValue += ":" + value.slice(2);
      }
    }

    // Setting the value in the component/parent state
    setSelectedInputValue(maskedValue);
    setSelectedLatestTime?.(maskedValue);
  };

  // User can only type numbers or use backspace/delete keys in the input
  const validateInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const re = /[0-9]+/g;
    if (
      event.key === "Backspace" ||
      event.key === "Delete" ||
      re.test(event.key)
    ) {
      return event;
    } else {
      event.preventDefault();
    }
  };

  const setFocus = () => {
    setIsInputTouched(true);
    // Setting timestamp when the input gets the focus. This will be stored in the local storage
    // and used to calculate when to set the refresh time active (after 10 minutes of inactivity)
    // and still with the focus in the text input
    setInputWithFocusTimestamp(dayjs());
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      let currentTime = dayjs();
      // Checking if the focus has been in the input for more than X minutes. If so, the timer
      // refresh should restart
      if (isInputTouched) {
        let futureInputWithFocusTimestamp = inputWithFocusTimestamp.add(
          waitingMinutesBeforeResettingTimer,
          "minutes"
        );
        if (currentTime >= futureInputWithFocusTimestamp) {
          setIsInputTouched(false);
          timePickerRef.current?.blur();
        }
      } else {
        // When the time input is not with the focus, the value will be refreshed with the current
        // time. As soon as the input gets the focus, the refreshing time stops
        let currentTimeString = currentTime.format("HH:mm");
        setSelectedInputValue(currentTimeString);
        setSelectedLatestTime?.(currentTimeString);
      }
    }, 1000);

    if (removeFocusFromTimeInput) {
      // Removing focus in input
      setIsInputTouched(false);
      // Resetting removeFocus variable in parent component to false value
      // So it can be changed next time the calculate / reset new patient buttons are pressed
      setRemoveFocusInParentComponent(false);
      // Setting the focus input timestamp to empty value since we are removing
      // the focus effect
      setInputWithFocusTimestamp("");
    }

    return () => clearInterval(intervalId);
  }, [
    inputWithFocusTimestamp,
    isInputTouched,
    removeFocusFromTimeInput,
    setInputWithFocusTimestamp,
    setRemoveFocusInParentComponent,
    setSelectedLatestTime,
  ]);

  return (
    <div className="time-picker-container">
      <TextField
        id="TimePickerInputField"
        size={Size.Small}
        value={selectedInputValue}
        maxLength={5}
        onChange={(value) => handleInputChange(value)}
        onKeyDown={(event) => validateInput(event)}
        onFocus={() => setFocus()}
        ref={timePickerRef}
      />
      <div className="clock-icon">
        <SystemIcons.Time color={COLORS.neutral_600} />
      </div>
    </div>
  );
};

export default TimePicker;
